import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Location, TitleCasePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { SocketService } from '../../../services/socket.service';
import {
    AuctionCatalogsService,
    AuctionDetail,
    GetAuctionById200Response,
    GetLotById200Response,
    GetRemindersByAccountMappingsId200Response,
    NotificationsService,
    Reminder,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { TranslateService } from '@ngx-translate/core';
import { MetaTagsService } from '@/shared/services/meta-tags.service';
import { DeviceCheckService } from '../../../services/device-check.service';
import { BiddingUtils, InstructionData } from '@/vb-utils/bidding.util';

interface BreadcrumbItem {
    label: string;
    /** Router commands to navigate to, or null for the current (last) crumb. */
    commands: string[] | null;
}

@Component({
    selector: 'vb-breadcrumb-one',
    templateUrl: './breadcrumb-one.component.html',
    styleUrls: ['./breadcrumb-one.component.scss'],
})
export class BreadcrumbOneComponent implements OnInit, OnDestroy {
    private readonly destroy$: Subject<void> = new Subject<void>();
    @Input() title!: string;
    /** Optional intermediate crumbs between Home and the current page (title
     *  mode only) — e.g. the listing page a detail page belongs under, so
     *  "Your Order" can read Home › My Invoices › Your Order. */
    @Input() parents: { label: string; commands: string[] }[] = [];
    @Input() subtitle!: string;
    @Input() full_width = false;
    @Input() shop_1600 = false;
    @Input() disableTitle: boolean = false;
    @Input() currentUserId: string = '';
    /** Steps the title up to the hero display size (auction lot list). */
    @Input() heroTitle: boolean = false;
    auction!: AuctionDetail;
    public isLoading: boolean = true;
    public unableToLoad: boolean = false;
    public auctionTitle: string = '';
    public auctionId: string | null = null;
    public lotTitle: string = '';
    public lotId: string | null = null;
    isWatching: boolean = false;
    reminders: Reminder[] = [];
    showReminderDialog: boolean = false;
    instruction?: InstructionData;

    constructor(
        private location: Location,
        public router: Router,
        private route: ActivatedRoute,
        private auctionService: AuctionCatalogsService,
        private notificationsService: NotificationsService,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private metaTagsService: MetaTagsService,
        private deviceCheckService: DeviceCheckService,
        private readonly biddingUtils: BiddingUtils,
        private socketService: SocketService,
    ) {}

    ngOnInit(): void {
        this.auctionId = this.route.snapshot.paramMap.get('auctionId');
        this.lotId = this.route.snapshot.paramMap.get('lotId');

        this.deviceCheckService.isSlow().then((isSlow: boolean) => {
            if (isSlow) {
                this.snackbarService.system(
                    this.translate.instant(
                        'Notifications.slowConnectionDetected',
                    ),
                );
            }
        });

        if (this.lotId != null) {
            this.auctionService
                .getLotById(this.lotId)
                .subscribe((res: GetLotById200Response) => {
                    this.lotTitle = res.data.title ?? '';
                });
        }

        if (this.auctionId != null) {
            this.auctionService
                .getAuctionById(this.auctionId)
                .subscribe((res: GetAuctionById200Response) => {
                    this.applyAuctionMeta(res.data);
                    if (this.currentUserId) {
                        this.fetchReminderList();
                    }
                });
        }

        this.socketService.resource$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: string) => {
                try {
                    const event: { action?: string; data?: AuctionDetail } =
                        JSON.parse(data);
                    if (
                        event.action === 'AUCTION_UPDATE' &&
                        event.data?.id &&
                        event.data.id === this.auctionId
                    ) {
                        this.applyAuctionMeta(event.data);
                    }
                } catch {
                    /* silent */
                }
            });

        this.isLoading = false;
        this.biddingUtils
            .getInstructionForCurrentPage$(window.location.href)
            .subscribe((instruction: InstructionData | null) => {
                this.instruction = instruction ?? undefined;
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private applyAuctionMeta(detail: AuctionDetail): void {
        this.auctionTitle = detail.name ?? '';
        this.auction = detail;
        if (this.router.url.includes('/lot/')) {
            return;
        }
        this.metaTagsService.setTitleTag(this.auctionTitle, true);
        this.metaTagsService.setDescriptionTag(this.auctionTitle);
        this.metaTagsService.setImageTag(
            this.metaTagsService.getDefaultOgImage,
        );
    }

    goBack(): void {
        this.location.back();
    }

    /**
     * Builds the breadcrumb trail for the slim vb-breadcrumb. The last item is
     * the current page (no link); earlier items carry router commands. Mirrors
     * the legacy span-based logic: Home › All Auctions › auction › lot/title.
     */
    get breadcrumbItems(): BreadcrumbItem[] {
        const titlecase: TitleCasePipe = new TitleCasePipe();
        const items: BreadcrumbItem[] = [
            { label: 'Home', commands: ['/home'] },
        ];

        if (this.title) {
            for (const parent of this.parents) {
                items.push({
                    label: titlecase.transform(parent.label),
                    commands: parent.commands,
                });
            }
            items.push({
                label: titlecase.transform(this.title),
                commands: null,
            });
            return items;
        }

        if (this.auctionId) {
            items.push({ label: 'All Auctions', commands: ['/auctions'] });
            // Auction and lot titles are seller data — shown raw everywhere
            // (cards, details, tables), so the crumb trail matches.
            if (this.auctionTitle) {
                items.push({
                    label: this.auctionTitle,
                    commands: this.lotId ? ['/auctions', this.auctionId] : null,
                });
            }
            if (this.lotId && this.lotTitle) {
                items.push({
                    label: this.lotTitle,
                    commands: null,
                });
            }
            return items;
        }

        items.push({ label: 'All Auctions', commands: null });
        return items;
    }

    get crumbTrail(): string[] {
        return this.breadcrumbItems.map((item: BreadcrumbItem) => item.label);
    }

    onCrumbSelect(index: number): void {
        const item: BreadcrumbItem | undefined = this.breadcrumbItems[index];
        if (item?.commands) {
            this.router.navigate(item.commands);
        }
    }

    showSchedulerDialog(): void {
        this.showReminderDialog = true;
    }

    closeReminderDialog(): void {
        this.showReminderDialog = false;
    }

    onRemindersChange(reminders: Reminder[]): void {
        this.reminders = reminders;
        this.isWatching = this.reminders.length > 0;
    }

    showToolTipText(): string {
        if (this.isWatching)
            return this.translate.instant(
                'AuctionsPage.manageAuctionReminders',
            );
        else return this.translate.instant('AuctionsPage.setAuctionReminder');
    }

    fetchReminderList(): void {
        this.notificationsService
            .getRemindersByAccountMappingsId(1, -1, this.auction.id)
            .subscribe((data: GetRemindersByAccountMappingsId200Response) => {
                this.reminders = data.data;
                this.isWatching = this.reminders.length > 0;
            });
    }
}
